"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import {
  FileText,
  Building2,
  Briefcase,
  Shield,
  CreditCard,
  Handshake,
  TrendingUp,
  ScrollText,
  PenLine,
  Landmark,
  Wallet,
  Home,
  ChevronRight,
  Check,
  AlertTriangle,
  Lightbulb,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TemplateSection {
  title: string;
  description: string;
  mustHave: boolean;
}

interface TemplateBestPractice {
  text: string;
  type: "do" | "dont";
}

interface DocumentTemplate {
  id: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  sections: TemplateSection[];
  mustHaveClauses: string[];
  bestPractices: TemplateBestPractice[];
  sampleLanguage: Array<{ clause: string; sample: string }>;
}

const TEMPLATES: Record<string, DocumentTemplate> = {
  rental: {
    id: "rental",
    icon: Home,
    color: "text-blue-600 dark:text-blue-400",
    sections: [
      { title: "Parties & Property Details", description: "Full names, Aadhaar/PAN, property address with survey number, landlord ownership proof reference", mustHave: true },
      { title: "Term & Renewal", description: "Start date, end date, renewal mechanism (automatic vs. manual), lock-in period for both parties", mustHave: true },
      { title: "Rent & Escalation", description: "Monthly rent, due date, payment mode, annual escalation % (typical: 5-10%), rent receipt process", mustHave: true },
      { title: "Security Deposit", description: "Amount (typical: 2-10 months depending on city), refund timeline (max 30 days), deduction conditions", mustHave: true },
      { title: "Maintenance & Repairs", description: "Who pays for what — structural repairs (landlord), day-to-day maintenance (tenant), appliance repairs", mustHave: true },
      { title: "Termination & Notice", description: "Notice period (1-3 months), early exit penalties, handover condition requirements", mustHave: true },
      { title: "Usage Restrictions", description: "Residential only, no subletting, pet policy, maximum occupants, alteration permissions", mustHave: false },
      { title: "Dispute Resolution", description: "Jurisdiction (local court), mediation first, arbitration clause for faster resolution", mustHave: true },
      { title: "Stamp Duty & Registration", description: "Who bears the cost, registration timeline (mandatory if >11 months), e-registration details", mustHave: true },
    ],
    mustHaveClauses: [
      "Notice period clause — at least 1 month for either party",
      "Security deposit refund timeline — max 30 days after vacating",
      "Maintenance responsibility split (structural vs. regular)",
      "Rent escalation cap (standard: 5-10% per annum)",
      "Termination conditions and early exit terms",
      "Force majeure clause for unexpected situations",
      "Dispute resolution mechanism",
      "Inventory checklist (to be attached as annexure)",
    ],
    bestPractices: [
      { text: "Register agreements exceeding 11 months — it's legally mandatory and protects both parties", type: "do" },
      { text: "Attach a detailed inventory list with photographs of the property condition", type: "do" },
      { text: "Include the landlord's property ownership proof reference (sale deed/index-II number)", type: "do" },
      { text: "Specify the exact date for rent payment and acceptable payment modes", type: "do" },
      { text: "Sign on an agreement with no termination clause — you could be locked in indefinitely", type: "dont" },
      { text: "Accept verbal promises about repairs or modifications — get everything in writing", type: "dont" },
      { text: "Skip stamp duty — unstamped agreements are inadmissible as evidence in court", type: "dont" },
      { text: "Agree to a security deposit refund 'at landlord's discretion' — specify clear terms", type: "dont" },
    ],
    sampleLanguage: [
      {
        clause: "Notice Period",
        sample: "Either party may terminate this agreement by providing not less than [2 months] written notice to the other party, delivered to the registered address mentioned herein.",
      },
      {
        clause: "Security Deposit Refund",
        sample: "The Lessor shall refund the security deposit within [30 days] of the Lessee vacating the premises, after deducting any amounts due towards unpaid rent, utility charges, or damages beyond normal wear and tear, supported by photographic evidence.",
      },
      {
        clause: "Rent Escalation",
        sample: "The monthly rent shall be increased by [5%] at the end of each year of the tenancy. Such increase shall take effect from the anniversary date of commencement of this lease.",
      },
    ],
  },
  employment: {
    id: "employment",
    icon: Briefcase,
    color: "text-purple-600 dark:text-purple-400",
    sections: [
      { title: "Parties & Position", description: "Employer details (CIN, registered address), employee name, designation, department, reporting manager", mustHave: true },
      { title: "Compensation", description: "CTC breakdown (basic, HRA, special allowance, variable), payment date, bank details, tax deduction", mustHave: true },
      { title: "Working Hours & Leave", description: "Weekly hours, flexible working, leave policy (CL, SL, EL, maternity/paternity), holidays, overtime", mustHave: true },
      { title: "Probation & Confirmation", description: "Probation period (typically 3-6 months), confirmation process, notice period during probation", mustHave: true },
      { title: "Notice Period & Exit", description: "Notice period post-confirmation (1-3 months), notice buy-out, handover process, exit interview", mustHave: true },
      { title: "Confidentiality & IP", description: "Definition of confidential information, IP assignment, invention disclosure, post-employment obligations", mustHave: true },
      { title: "Non-Compete & Non-Solicitation", description: "Restrictions on joining competitors, soliciting clients/employees (must be reasonable in scope and duration)", mustHave: false },
      { title: "Benefits & Insurance", description: "PF, gratuity, medical insurance (coverage amount, family inclusion), bonus structure, ESOP if applicable", mustHave: true },
      { title: "Termination", description: "Grounds for termination, misconduct definition, process (show cause → enquiry), severance if applicable", mustHave: true },
    ],
    mustHaveClauses: [
      "CTC breakdown with clear basic, HRA, and variable split",
      "Notice period clause — symmetrical for both employer and employee",
      "PF and gratuity compliance (as per EPF Act and Payment of Gratuity Act)",
      "Confidentiality clause with reasonable scope and duration",
      "Termination process with grounds and procedure",
      "Leave policy aligned with state Shops & Establishments Act",
      "Dispute resolution and governing law",
      "Background verification consent clause",
    ],
    bestPractices: [
      { text: "Ensure the notice period is symmetrical — if you give 3 months, the employer should too", type: "do" },
      { text: "Get the CTC breakdown in writing, with clear variable/bonus trigger criteria", type: "do" },
      { text: "Verify PF contributions match statutory requirements (12% of basic by both employer and employee)", type: "do" },
      { text: "Accept an unreasonably broad non-compete clause — they're largely unenforceable in India but can still cause hassle", type: "dont" },
      { text: "Ignore IP assignment clauses — they can claim ownership of your side projects if broadly worded", type: "dont" },
      { text: "Sign without understanding the variable pay trigger criteria and payout timeline", type: "dont" },
    ],
    sampleLanguage: [
      {
        clause: "Notice Period",
        sample: "Either party may terminate this employment by providing [90 days] written notice. The Company may, at its discretion, waive the notice period in part or full, with payment of salary in lieu of the notice period.",
      },
      {
        clause: "Intellectual Property",
        sample: "All work product, inventions, and creations made by the Employee during the course of employment and using the Company's resources shall be the exclusive property of the Company. Personal projects created outside work hours using personal resources are excluded.",
      },
    ],
  },
  loan: {
    id: "loan",
    icon: Landmark,
    color: "text-green-600 dark:text-green-400",
    sections: [
      { title: "Parties & Loan Details", description: "Lender (RBI license number), borrower details, co-borrower/guarantor, loan account number", mustHave: true },
      { title: "Loan Amount & Disbursement", description: "Sanctioned amount, disbursement schedule (lump sum vs. tranches), pre-disbursement conditions", mustHave: true },
      { title: "Interest Rate", description: "Rate type (fixed/floating), benchmark rate (repo + spread), reset frequency, effective annual rate", mustHave: true },
      { title: "Repayment Schedule", description: "EMI amount, tenure, moratorium period, payment date, mode of payment, amortization table reference", mustHave: true },
      { title: "Prepayment & Foreclosure", description: "Prepayment terms, foreclosure charges (nil for floating rate per RBI), part-payment limits", mustHave: true },
      { title: "Charges & Fees", description: "Processing fee, legal charges, insurance premium, documentation charges, stamp duty, late payment penalty", mustHave: true },
      { title: "Security/Collateral", description: "Property/asset pledged, valuation, creation of charge, CERSAI registration, insurance requirement", mustHave: true },
      { title: "Default & Remedies", description: "Events of default, SARFAESI Act applicability, recall of loan, recovery process, NPA classification", mustHave: true },
    ],
    mustHaveClauses: [
      "Interest rate type and calculation method (reducing balance is standard)",
      "Prepayment charges — RBI mandates nil charges on floating rate loans",
      "All fees and charges listed upfront (processing, legal, documentation)",
      "Default definition and cure period before NPA classification",
      "Insurance requirements and premium responsibility",
      "Security/collateral terms with release conditions after full repayment",
      "Dispute resolution mechanism",
    ],
    bestPractices: [
      { text: "Always ask for reducing balance rate — flat rate is effectively ~1.8x more expensive", type: "do" },
      { text: "Compare MCLR/repo-linked rates across 3-4 lenders before committing", type: "do" },
      { text: "Read the sanction letter carefully — it has all the specific terms for YOUR loan", type: "do" },
      { text: "Accept a loan with high processing fees without negotiating — most lenders will reduce on request", type: "dont" },
      { text: "Skip the amortization schedule — understand how much goes to interest vs. principal each year", type: "dont" },
      { text: "Ignore the insurance clause — lender-mandated insurance is often overpriced, you can opt for your own policy", type: "dont" },
    ],
    sampleLanguage: [
      {
        clause: "Prepayment",
        sample: "The Borrower may prepay the outstanding loan amount, in part or full, without any prepayment penalty or foreclosure charges, in compliance with RBI circular DNBR.PD.007/03.10.119/2016-17 and subsequent amendments.",
      },
    ],
  },
  nda: {
    id: "nda",
    icon: Shield,
    color: "text-red-600 dark:text-red-400",
    sections: [
      { title: "Parties", description: "Disclosing party, receiving party, or mutual NDA (both are disclosers and receivers)", mustHave: true },
      { title: "Definition of Confidential Information", description: "What's covered — documents, data, trade secrets, business plans, source code, customer lists", mustHave: true },
      { title: "Exclusions", description: "Publicly available info, independently developed, received from third party, required by law/court", mustHave: true },
      { title: "Obligations", description: "Non-disclosure, non-use (only for intended purpose), protection standard (reasonable care), limited sharing", mustHave: true },
      { title: "Term", description: "Duration of agreement, survival period after termination (typically 2-5 years)", mustHave: true },
      { title: "Return/Destruction", description: "Return or destroy confidential materials on termination, certification of destruction", mustHave: true },
      { title: "Remedies", description: "Injunctive relief for breach, indemnification, jurisdiction for legal proceedings", mustHave: true },
    ],
    mustHaveClauses: [
      "Clear definition of what constitutes 'Confidential Information'",
      "Standard exclusions (public info, independently developed, etc.)",
      "Term and survival period",
      "Obligation to return or destroy materials after termination",
      "Remedies for breach including injunctive relief",
      "Governing law and jurisdiction",
    ],
    bestPractices: [
      { text: "Make it mutual if both parties share sensitive information", type: "do" },
      { text: "Be specific about what's confidential — vague definitions invite disputes", type: "do" },
      { text: "Include standard exclusions to protect yourself from inadvertent breaches", type: "do" },
      { text: "Sign an NDA that defines everything as confidential — it's unenforceable and impractical", type: "dont" },
      { text: "Agree to indefinite confidentiality obligations — 3-5 years is reasonable for most situations", type: "dont" },
    ],
    sampleLanguage: [
      {
        clause: "Exclusions",
        sample: "Confidential Information shall not include information that: (a) is or becomes publicly available through no fault of the Receiving Party; (b) was already known to the Receiving Party prior to disclosure; (c) is independently developed without reference to the Confidential Information; or (d) is required to be disclosed by law, regulation, or court order.",
      },
    ],
  },
  insurance: {
    id: "insurance",
    icon: Shield,
    color: "text-teal-600 dark:text-teal-400",
    sections: [
      { title: "Policy Details", description: "Policy number, insurer details (IRDAI registration), insured details, nominee, policy term", mustHave: true },
      { title: "Coverage", description: "Sum insured, coverage type (individual/floater), sub-limits, room rent limits, co-payment", mustHave: true },
      { title: "Exclusions", description: "Pre-existing conditions waiting period, permanent exclusions, age limits, specific disease waiting periods", mustHave: true },
      { title: "Premium", description: "Annual premium, payment schedule, grace period (typically 30 days), premium adjustment on claim", mustHave: true },
      { title: "Claim Process", description: "Cashless vs reimbursement, network hospitals, documentation required, claim settlement timeline", mustHave: true },
      { title: "Free Look Period", description: "15-day period to cancel and get full refund (IRDAI mandated)", mustHave: true },
      { title: "Renewal Terms", description: "Lifetime renewability, no-claim bonus, portability option, premium revision terms", mustHave: true },
    ],
    mustHaveClauses: [
      "Sum insured and sub-limits clearly stated",
      "Complete list of exclusions and waiting periods",
      "Free-look period (15 days per IRDAI regulations)",
      "Claim process with timelines for settlement",
      "Renewal terms and no-claim bonus details",
      "Portability clause",
    ],
    bestPractices: [
      { text: "Check sub-limits for room rent, specific diseases, and procedures — they can significantly reduce effective coverage", type: "do" },
      { text: "Understand the co-payment clause — you may need to pay 10-20% of every claim", type: "do" },
      { text: "Verify the network hospital list for hospitals near your home and workplace", type: "do" },
      { text: "Buy insurance just for the tax benefit — actual coverage should be the priority", type: "dont" },
      { text: "Hide pre-existing conditions — non-disclosure can lead to claim rejection and policy cancellation", type: "dont" },
    ],
    sampleLanguage: [],
  },
  partnership: {
    id: "partnership",
    icon: Handshake,
    color: "text-orange-600 dark:text-orange-400",
    sections: [
      { title: "Partners & Firm Details", description: "Partner names, addresses, PAN, firm name, principal place of business, nature of business", mustHave: true },
      { title: "Capital Contributions", description: "Each partner's contribution (cash, assets, goodwill), interest on capital, additional capital calls", mustHave: true },
      { title: "Profit & Loss Sharing", description: "Sharing ratio, drawing rights, salary/remuneration to working partners, interest on drawings", mustHave: true },
      { title: "Management & Authority", description: "Decision-making process, banking authority, signing authority, day-to-day management roles", mustHave: true },
      { title: "Admission & Retirement", description: "Process for admitting new partners, retirement/exit, goodwill valuation, non-compete on exit", mustHave: true },
      { title: "Dissolution", description: "Events triggering dissolution, winding up process, distribution of assets, settlement of liabilities", mustHave: true },
    ],
    mustHaveClauses: [
      "Clear profit/loss sharing ratio for each partner",
      "Capital contribution details and interest terms",
      "Decision-making and authority matrix",
      "Exit/retirement clause with goodwill valuation method",
      "Non-compete clause for departing partners",
      "Dispute resolution mechanism",
      "Death/incapacity clause",
    ],
    bestPractices: [
      { text: "Register the deed with the Registrar of Firms for better legal standing", type: "do" },
      { text: "Define the goodwill valuation method upfront — it prevents disputes when a partner exits", type: "do" },
      { text: "Leave partner liability terms ambiguous — each partner can be held liable for firm's debts in a general partnership", type: "dont" },
      { text: "Skip the death/incapacity clause — it creates chaos for surviving partners", type: "dont" },
    ],
    sampleLanguage: [],
  },
  startup: {
    id: "startup",
    icon: TrendingUp,
    color: "text-indigo-600 dark:text-indigo-400",
    sections: [
      { title: "Parties & Company Details", description: "Investor details, company details (CIN), founders, existing cap table", mustHave: true },
      { title: "Investment Terms", description: "Investment amount, pre/post-money valuation, instrument type (equity/CCPS/CCD/SAFE), tranche conditions", mustHave: true },
      { title: "Board & Governance", description: "Board composition, investor director rights, observer rights, reserved matters requiring investor consent", mustHave: true },
      { title: "Founder Terms", description: "Vesting schedule, cliff period, ESOP pool, full-time commitment, non-compete, IP assignment", mustHave: true },
      { title: "Protective Provisions", description: "Anti-dilution (weighted average vs. full ratchet), liquidation preference, drag-along, tag-along", mustHave: true },
      { title: "Information Rights", description: "Financial reporting frequency, audit rights, investor updates, access to records", mustHave: true },
      { title: "Exit", description: "IPO terms, secondary sale rights, buyback, right of first refusal (ROFR)", mustHave: true },
    ],
    mustHaveClauses: [
      "Valuation and investment amount clearly stated",
      "Anti-dilution protection terms (weighted average is founder-friendly)",
      "Liquidation preference (1x non-participating is standard)",
      "Founder vesting with cliff (standard: 4-year vest, 1-year cliff)",
      "Reserved matters / consent rights for investors",
      "ESOP pool size and expansion terms",
      "Information rights and reporting obligations",
    ],
    bestPractices: [
      { text: "Negotiate for weighted average anti-dilution instead of full ratchet — it's much fairer to founders", type: "do" },
      { text: "Keep liquidation preference at 1x non-participating — anything higher significantly reduces founder upside", type: "do" },
      { text: "Include a defined ESOP pool in the pre-money valuation to avoid post-money dilution surprises", type: "do" },
      { text: "Accept participating preferred with high liquidation multiples — it can eat into founder returns significantly", type: "dont" },
      { text: "Ignore reserved matters — over-broad consent rights can paralyze the company", type: "dont" },
    ],
    sampleLanguage: [
      {
        clause: "Anti-Dilution",
        sample: "In the event of a Down Round, the Investor's conversion price shall be adjusted using the broad-based weighted average method, calculated as: Adjusted Price = Original Price × (A + B) / (A + C), where A = fully diluted shares before the new round, B = shares that would be issued at the original conversion price, C = shares actually issued in the Down Round.",
      },
    ],
  },
  vendor: {
    id: "vendor",
    icon: ScrollText,
    color: "text-cyan-600 dark:text-cyan-400",
    sections: [
      { title: "Parties & Scope", description: "Service provider and client details, detailed scope of work/deliverables, timeline, milestones", mustHave: true },
      { title: "Payment Terms", description: "Pricing model (fixed/T&M), payment schedule, invoicing process, late payment interest, GST", mustHave: true },
      { title: "SLAs & Performance", description: "Service levels, uptime guarantees, response times, penalty for SLA breach, measurement methodology", mustHave: true },
      { title: "IP & Data", description: "IP ownership (work product, pre-existing IP), data handling, privacy compliance, return of data", mustHave: true },
      { title: "Liability & Indemnity", description: "Liability cap, indemnification obligations, insurance requirements, consequential damage exclusion", mustHave: true },
      { title: "Termination", description: "Termination for convenience (with notice), termination for cause, transition assistance, wind-down", mustHave: true },
    ],
    mustHaveClauses: [
      "Detailed scope of work or statement of work (SOW)",
      "Payment terms with clear timelines and late payment consequences",
      "SLA definitions with measurable metrics",
      "IP ownership of deliverables",
      "Data protection and confidentiality terms",
      "Liability cap (typically 12 months of fees)",
      "Termination for convenience with reasonable notice",
      "Dispute resolution mechanism",
    ],
    bestPractices: [
      { text: "Attach a detailed SOW as an annexure — vague scope leads to disputes", type: "do" },
      { text: "Include a termination for convenience clause — business needs change", type: "do" },
      { text: "Cap liability at a reasonable amount (12 months of fees is market standard)", type: "do" },
      { text: "Accept unlimited liability — even large enterprises cap their exposure", type: "dont" },
      { text: "Skip the transition/wind-down clause — you'll regret it when switching vendors", type: "dont" },
    ],
    sampleLanguage: [],
  },
  freelance: {
    id: "freelance",
    icon: PenLine,
    color: "text-pink-600 dark:text-pink-400",
    sections: [
      { title: "Parties & Engagement", description: "Freelancer and client details, project description, start/end dates, independent contractor status", mustHave: true },
      { title: "Scope & Deliverables", description: "Detailed deliverables, milestones, acceptance criteria, revision rounds included, change request process", mustHave: true },
      { title: "Compensation", description: "Fee (fixed/hourly), payment milestones, invoicing process, payment timeline (NET 15/30), GST", mustHave: true },
      { title: "IP & Usage Rights", description: "Who owns the work product, license terms, portfolio usage rights, source files handover", mustHave: true },
      { title: "Confidentiality", description: "Non-disclosure of client's business information, reasonable scope and duration", mustHave: true },
      { title: "Termination", description: "Kill fee (payment for work completed), notice period, handover of work-in-progress", mustHave: true },
    ],
    mustHaveClauses: [
      "Clear scope of work with defined deliverables",
      "Payment terms with milestones or schedule",
      "IP ownership transfer on full payment",
      "Revision/iteration limits (e.g., 2 rounds of revisions included)",
      "Termination clause with kill fee for work completed",
      "Independent contractor status (not employment)",
      "Confidentiality clause",
    ],
    bestPractices: [
      { text: "Define revision rounds upfront — unlimited revisions is a recipe for scope creep", type: "do" },
      { text: "Include a kill fee — if the project is cancelled, you should be paid for work done", type: "do" },
      { text: "Transfer IP only after full payment is received", type: "do" },
      { text: "Start work without a signed agreement, even for 'quick' projects", type: "dont" },
      { text: "Accept NET 90 payment terms — NET 15 or NET 30 is reasonable for freelancers", type: "dont" },
    ],
    sampleLanguage: [],
  },
};

const TEMPLATE_ORDER = [
  "rental", "employment", "loan", "nda", "insurance",
  "partnership", "startup", "vendor", "freelance",
];

const TEMPLATE_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  rental: Home,
  employment: Briefcase,
  loan: Landmark,
  nda: Shield,
  insurance: Shield,
  partnership: Handshake,
  startup: TrendingUp,
  vendor: ScrollText,
  freelance: PenLine,
};

export default function TemplatesPage() {
  const t = useTranslations("templates");
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const template = selectedType ? TEMPLATES[selectedType] : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-24">
      {/* Header */}
      <div>
        <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
          <FileText size={18} className="text-brand" />
          {t("title")}
        </h1>
        <p className="text-xs text-muted-foreground mt-1">{t("subtitle")}</p>
      </div>

      {!selectedType && (
        <>
          {/* Template grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TEMPLATE_ORDER.map((type) => {
              const tmpl = TEMPLATES[type];
              if (!tmpl) return null;
              const Icon = TEMPLATE_ICONS[type] || FileText;

              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className="rounded-xl border border-border bg-card p-4 text-left hover:border-brand/50 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={cn("rounded-lg bg-muted/50 p-2", tmpl.color)}>
                      <Icon size={18} />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {t(`types.${type}`)}
                    </h3>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    {tmpl.sections.length} {t("sections")} • {tmpl.mustHaveClauses.length} {t("mustHaveClauses")}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-[10px] text-brand font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    {t("viewTemplate")}
                    <ChevronRight size={10} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Disclaimer */}
          <p className="text-[10px] text-muted-foreground text-center italic">
            {t("disclaimer")}
          </p>
        </>
      )}

      {/* Selected template detail */}
      {template && selectedType && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <button
            onClick={() => setSelectedType(null)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={12} />
            Back to all templates
          </button>

          <div className="flex items-center gap-3">
            <div className={cn("rounded-lg bg-muted/50 p-2.5", template.color)}>
              <template.icon size={22} />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">
                {t(`types.${selectedType}`)}
              </h2>
              <p className="text-xs text-muted-foreground">
                {template.sections.length} {t("sections")} • {template.mustHaveClauses.length} {t("mustHaveClauses")}
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">{t("sections")}</h3>
            {template.sections.map((section, i) => (
              <div
                key={i}
                className={cn(
                  "rounded-lg border p-3.5 space-y-1",
                  section.mustHave
                    ? "border-brand/30 bg-brand/5"
                    : "border-border bg-card"
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-foreground">
                    {i + 1}. {section.title}
                  </span>
                  {section.mustHave && (
                    <span className="text-[9px] font-medium text-brand bg-brand/10 rounded px-1.5 py-0.5">
                      Required
                    </span>
                  )}
                </div>
                <p className="text-xs text-foreground/70 leading-relaxed">
                  {section.description}
                </p>
              </div>
            ))}
          </div>

          {/* Must-have clauses */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Check size={14} className="text-green-500" />
              {t("mustHaveClauses")}
            </h3>
            <div className="space-y-2">
              {template.mustHaveClauses.map((clause, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <Check size={12} className="text-green-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground/80">{clause}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Best practices */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Lightbulb size={14} className="text-brand" />
              {t("bestPractices")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {template.bestPractices.map((bp, i) => (
                <div
                  key={i}
                  className={cn(
                    "rounded-lg border p-3 space-y-1",
                    bp.type === "do"
                      ? "border-green-200 dark:border-green-900/30 bg-green-50/50 dark:bg-green-950/10"
                      : "border-red-200 dark:border-red-900/30 bg-red-50/50 dark:bg-red-950/10"
                  )}
                >
                  <span
                    className={cn(
                      "text-[9px] font-bold uppercase",
                      bp.type === "do"
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    )}
                  >
                    {bp.type === "do" ? "✓ DO" : "✗ DON'T"}
                  </span>
                  <p className="text-xs text-foreground/80 leading-relaxed">
                    {bp.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Sample language */}
          {template.sampleLanguage.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <ScrollText size={14} className="text-brand" />
                {t("sampleLanguage")}
              </h3>
              {template.sampleLanguage.map((sample, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-border bg-muted/20 p-4 space-y-2"
                >
                  <p className="text-[10px] font-semibold text-brand uppercase tracking-wider">
                    {sample.clause}
                  </p>
                  <p className="text-xs text-foreground/80 leading-relaxed italic">
                    &ldquo;{sample.sample}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <div className="rounded-lg bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200/50 dark:border-amber-900/20 p-3 flex items-start gap-2">
            <AlertTriangle size={12} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-foreground/70">{t("disclaimer")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
